import { useMutation } from '@apollo/client';
import { isSameDay } from 'date-fns';
import UPDATE_POST_DATE from '../graphql/updatePostDate';
import GET_CALENDAR_MONTH_QUERY from '../graphql/month';
import getFormattedTime from '../../utils/getFormattedTime';

const reorderPosts = (days, updatedPost) => {
  const daysAfterRemove = days.map(day => {
    const { posts } = day;
    return {
      ...day,
      posts: posts.filter(post => post.id !== updatedPost.id),
    };
  });

  const result = daysAfterRemove.map(day => {
    if (isSameDay(day.startTimestamp * 1000, updatedPost.dueAt * 1000)) {
      return {
        ...day,
        posts: [...day.posts, updatedPost],
      };
    }
    return day;
  });

  return result;
};

export default function useDropPostInMonth({ onSuccess, onError, variables }) {
  const [updatePostDueAt] = useMutation(UPDATE_POST_DATE, {
    onCompleted: data => {
      if (data?.updatePostDueAt?.success) {
        onSuccess(data);
      }

      if (data?.updatePostDueAt?.success === false) {
        onError(data?.updatePostDueAt.message);
      }
    },
    onError: err => {
      onError(err);
    },
    update: (cache, mutationResult) => {
      const updatedPost = mutationResult.data.updatePostDueAt.post;
      const readData = cache.readQuery({
        query: GET_CALENDAR_MONTH_QUERY,
        variables,
      });

      const { days, posts } = readData.calendar;
      const postsReordered = reorderPosts(days, updatedPost);
      cache.writeQuery({
        query: GET_CALENDAR_MONTH_QUERY,
        variables,
        data: { calendar: { __typename: 'Calendar', posts, days: postsReordered } },
      });
    },
    refetchQueries: [GET_CALENDAR_MONTH_QUERY],
  });

  return {
    updatePostDueAt: ({ post, timestamp }) => {
      const formatted = getFormattedTime(
        timestamp * 1000,
        variables.timezone,
        variables.use24HourTime
      );

      updatePostDueAt({
        variables: { input: { postId: post.id, timestamp } },
        optimisticResponse: {
          updatePostDueAt: {
            success: true,
            message: 'Successfully updated post',
            post: {
              ...post,
              dueAt: timestamp,
              formattedTime: formatted,
            },
            __typename: 'PostUpdated',
          },
        },
      }).catch(e => console.error(e));
    },
  };
}
